import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import {
  BookOpen,
  Search,
  Filter,
  Calendar,
  Clock,
  CheckCircle2,
  XCircle,
  ArrowRight
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const Courses = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [coursesRes, enrollmentsRes] = await Promise.all([
        axios.get(`${API}/courses`),
        axios.get(`${API}/enrollments`)
      ]);
      setCourses(coursesRes.data);
      setEnrollments(enrollmentsRes.data);
    } catch (error) {
      toast.error("Error al cargar los cursos");
    } finally {
      setLoading(false);
    }
  };

  const getEnrollment = (courseId) => {
    return enrollments.find(e => e.course_id === courseId);
  };

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.category.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (statusFilter === "all") return matchesSearch;
    
    const enrollment = getEnrollment(course.id);
    if (!enrollment) return false;
    
    return matchesSearch && enrollment.status === statusFilter;
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 w-64 skeleton rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-72 skeleton rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="courses-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-heading">
            {user?.role === "ESTUDIANTE" ? "Mis Cursos" : "Cursos"}
          </h1>
          <p className="text-slate-600 mt-1">
            {filteredCourses.length} curso{filteredCourses.length !== 1 ? 's' : ''} disponible{filteredCourses.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Buscar cursos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-courses-input"
          />
        </div>
        {user?.role === "ESTUDIANTE" && (
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filtrar por estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="active">En Progreso</SelectItem>
              <SelectItem value="completed">Completados</SelectItem>
              <SelectItem value="failed">Reprobados</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Course Grid */}
      {filteredCourses.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No hay cursos disponibles</h3>
            <p className="text-slate-500">
              {searchTerm ? "No se encontraron cursos con ese criterio de búsqueda." : "Aún no estás matriculado en ningún curso."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCourses.map((course, index) => {
            const enrollment = getEnrollment(course.id);
            const progress = enrollment ? [
              enrollment.pretest_completed,
              enrollment.material_viewed,
              enrollment.posttest_completed
            ].filter(Boolean).length : 0;
            const progressPercent = (progress / 3) * 100;

            return (
              <Link 
                key={course.id} 
                to={`/courses/${course.id}`}
                data-testid={`course-item-${index}`}
              >
                <Card className="course-card h-full group">
                  <div className="h-36 bg-gradient-to-br from-primary to-blue-600 relative overflow-hidden">
                    <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors" />
                    <div className="absolute top-4 left-4">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-white/90 text-slate-700">
                        {course.category}
                      </span>
                    </div>
                    {enrollment && (
                      <div className="absolute bottom-4 left-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                          enrollment.status === 'completed' ? 'bg-green-500 text-white' :
                          enrollment.status === 'failed' ? 'bg-red-500 text-white' :
                          'bg-white/90 text-slate-700'
                        }`}>
                          {enrollment.status === 'completed' ? (
                            <><CheckCircle2 className="w-3.5 h-3.5" /> Completado</>
                          ) : enrollment.status === 'failed' ? (
                            <><XCircle className="w-3.5 h-3.5" /> Reprobado</>
                          ) : (
                            <><Clock className="w-3.5 h-3.5" /> En Progreso</>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <CardContent className="p-5">
                    <h3 className="font-semibold text-slate-900 mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                      {course.name}
                    </h3>
                    <p className="text-sm text-slate-500 line-clamp-2 mb-4">{course.description}</p>
                    
                    <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(course.start_date).toLocaleDateString()}</span>
                      </div>
                    </div>

                    {enrollment && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">Progreso</span>
                          <span className="font-medium text-slate-700">{Math.round(progressPercent)}%</span>
                        </div>
                        <Progress value={progressPercent} className="h-2" />
                      </div>
                    )}

                    <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                      <div className="flex gap-3 text-xs">
                        {enrollment?.pretest_score !== null && enrollment?.pretest_score !== undefined && (
                          <span className="text-slate-500">
                            Pre: <span className="font-medium text-slate-700">{enrollment.pretest_score.toFixed(0)}%</span>
                          </span>
                        )}
                        {enrollment?.posttest_score !== null && enrollment?.posttest_score !== undefined && (
                          <span className="text-slate-500">
                            Post: <span className="font-medium text-slate-700">{enrollment.posttest_score.toFixed(0)}%</span>
                          </span>
                        )}
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-primary group-hover:translate-x-1 transition-all" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Courses;
